import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Crown, Zap, Users, Calendar, CalendarDays, MessageSquare, Wrench, FileText, Smartphone, X, ArrowRight, ArrowLeft, Trash2, ShoppingCart, ListChecks, BarChart3, DollarSign, Megaphone, Gift, Package, Building2, Receipt, Folder, ExternalLink, RefreshCw, ChevronDown, Globe, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';
import {
  createNexusHostingCheckout,
  describeCardCharge,
  fetchNexusHostingStripeDetails,
  fetchNexusHostingMonthlyUsd,
  getNexusWebsiteHostingMonthlyUsd,
  isNexusHostingActive,
  type NexusHostingSubscriptionDetails,
} from '../lib/nexusHostingSubscription';
import CustomDomainPanel from '../components/CustomDomainPanel';
import MyAppsServiceHoursCard from '../components/MyAppsServiceHoursCard';
import { handleStripeCheckoutSuccess } from '../lib/stripeCheckout';
import { removePlatformTeamSeat } from '../lib/platformTeamSeats';
import { SOCIAL_CONNECTION_PRICE_LABEL } from '../lib/socialConnectionSeats';
import { SERVICE_HOUR_RATE_USD } from '../lib/serviceHours';
import { useBusiness } from '../contexts/BusinessContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAppSystem } from '../hooks/useAppSystem';
import toast from 'react-hot-toast';

interface SubscriptionPlan {
  plan_id: string;
  plan_name: string;
  plan_price: number;
  included_apps: string[];
  included_power_ups: string[];
  stripe_price_id?: string | null;
}

function formatHostingBillDate(iso: string | null | undefined, withYear = true): string {
  if (!iso || !String(iso).trim()) return '—';
  const t = Date.parse(String(iso));
  if (Number.isNaN(t)) return '—';
  return new Date(t).toLocaleDateString(
    'en-US',
    withYear ? { month: 'short', day: 'numeric', year: 'numeric' } : { month: 'short', day: 'numeric' },
  );
}

export default function MyApps() {
  const navigate = useNavigate();
  const { selectedBusiness, forceLoadBusinesses } = useBusiness();
  const { subscription, refreshSubscription, activateSubscription } = useSubscription();
  const { reload: reloadAppSystem } = useAppSystem();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlanForSwitch, setSelectedPlanForSwitch] = useState<SubscriptionPlan | null>(null);
  const [switchingPlan, setSwitchingPlan] = useState(false);
  const [addonSubscriptions, setAddonSubscriptions] = useState<any[]>([]);
  const [teamSeatAddon, setTeamSeatAddon] = useState<{ quantity: number; status: string } | null>(
    null,
  );
  const [socialSeatAddon, setSocialSeatAddon] = useState<{
    quantity: number;
    grandfathered_quantity: number;
    status: string;
  } | null>(null);
  const [serviceHourRetainer, setServiceHourRetainer] = useState<{
    quantity: number;
    status: string;
  } | null>(null);
  const [selectedAddonForCancel, setSelectedAddonForCancel] = useState<any | null>(null);
  const [cancellingAddon, setCancellingAddon] = useState(false);
  const [sessionUserId, setSessionUserId] = useState<string | null>(null);
  const [teamSeatRemoveConfirm, setTeamSeatRemoveConfirm] = useState(false);
  const [cancellingTeamSeat, setCancellingTeamSeat] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [activatingNexusHosting, setActivatingNexusHosting] = useState(false);
  const [hostingStripeDetails, setHostingStripeDetails] = useState<NexusHostingSubscriptionDetails | null>(
    null,
  );
  const [hostingStripeLoading, setHostingStripeLoading] = useState(false);
  const [includedAppsExpanded, setIncludedAppsExpanded] = useState(false);
  const [addonsExpanded, setAddonsExpanded] = useState(false);
  // Hosting display price from the subscription_plans row (same source as
  // checkout); starts on the static fallback until the fetch resolves.
  const [hostingMonthlyUsd, setHostingMonthlyUsd] = useState(getNexusWebsiteHostingMonthlyUsd());
  const forceLoadBusinessesRef = useRef(forceLoadBusinesses);
  forceLoadBusinessesRef.current = forceLoadBusinesses;

  useEffect(() => {
    fetchSubscriptionPlans();
    let cancelled = false;
    fetchNexusHostingMonthlyUsd().then((usd) => {
      if (!cancelled) setHostingMonthlyUsd(usd);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => {
      if (!cancelled) setSessionUserId(data.user?.id ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    fetchAddonSubscriptions();
  }, [selectedBusiness?.id]);

  useEffect(() => {
    const bid = selectedBusiness?.id;
    const sid = (selectedBusiness?.nexus_hosting_stripe_subscription_id || '').toString().trim();
    if (!bid || !sid) {
      setHostingStripeDetails(null);
      setHostingStripeLoading(false);
      return;
    }
    let cancelled = false;
    setHostingStripeLoading(true);
    fetchNexusHostingStripeDetails(bid)
      .then((d) => {
        if (!cancelled) setHostingStripeDetails(d);
      })
      .finally(() => {
        if (!cancelled) setHostingStripeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedBusiness?.id, selectedBusiness?.nexus_hosting_stripe_subscription_id]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('nexus_hosting') !== 'activated') return;
    const checkoutSessionId = params.get('session_id');
    params.delete('nexus_hosting');
    params.delete('session_id');
    const qs = params.toString();
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash || ''}`,
    );

    const finalize = async () => {
      if (checkoutSessionId) {
        try {
          const result = (await handleStripeCheckoutSuccess(checkoutSessionId)) as {
            success?: boolean;
            nexusHosting?: boolean;
          };
          if (!result?.success) {
            toast.error(
              'Could not confirm website hosting. If you completed payment, refresh in a moment or contact support.',
            );
          }
        } catch {
          toast.error(
            'Could not confirm website hosting. If you completed payment, refresh in a moment or contact support.',
          );
        }
      }
      forceLoadBusinessesRef.current();
    };
    void finalize();
  }, []);

  // Deep link from the "your website is ready" activation email
  // (/my-apps?activate_hosting=1): start the hosting checkout automatically
  // once the business is loaded. No-op when hosting is already active.
  const autoActivateHandledRef = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('activate_hosting') !== '1') return;
    if (!selectedBusiness?.id || autoActivateHandledRef.current) return;
    autoActivateHandledRef.current = true;
    params.delete('activate_hosting');
    const qs = params.toString();
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash || ''}`,
    );
    if (!isNexusHostingActive(selectedBusiness.nexus_hosting_subscription_status)) {
      void handleActivateNexusHosting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusiness?.id, selectedBusiness?.nexus_hosting_subscription_status]);

  const fetchSubscriptionPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('plan_price', { ascending: true });
      if (fetchError) { setError('Failed to load subscription plans'); return; }
      // Hosting rows (kind 'hosting', e.g. nexus-hosting) are billed separately
      // and must not appear as selectable platform plans. Plans without a
      // Stripe price yet aren't sellable, so they're hidden too.
      setPlans(
        (data || []).filter(
          (p) => p.kind !== 'hosting' && p.plan_id !== 'nexus-hosting' && !!p.stripe_price_id,
        ),
      );
    } catch {
      setError('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const fetchAddonSubscriptions = async () => {
    if (!selectedBusiness?.id) {
      setAddonSubscriptions([]);
      setTeamSeatAddon(null);
      setSocialSeatAddon(null);
      setServiceHourRetainer(null);
      return;
    }
    try {
      const [addonRes, seatRes, socialRes, hoursRes] = await Promise.all([
        supabase
          .from('business_addon_subscriptions')
          .select(
            `
          id, addon_app_id, status,
          addon_apps (
            id, name, price_monthly, app_id,
            app:app_id ( id, slug, name )
          )
        `,
          )
          .eq('business_id', selectedBusiness.id)
          .eq('status', 'active'),
        supabase
          .from('business_platform_team_seat_addons')
          .select('quantity, status')
          .eq('business_id', selectedBusiness.id)
          .maybeSingle(),
        supabase
          .from('business_social_connection_addons')
          .select('quantity, grandfathered_quantity, status')
          .eq('business_id', selectedBusiness.id)
          .maybeSingle(),
        supabase
          .from('business_service_hour_retainers')
          .select('quantity, status')
          .eq('business_id', selectedBusiness.id)
          .maybeSingle(),
      ]);

      if (!addonRes.error) setAddonSubscriptions(addonRes.data || []);
      else setAddonSubscriptions([]);

      if (!seatRes.error && seatRes.data) {
        setTeamSeatAddon({
          quantity: Number(seatRes.data.quantity) || 0,
          status: String(seatRes.data.status || ''),
        });
      } else {
        setTeamSeatAddon(null);
      }

      if (!socialRes.error && socialRes.data) {
        setSocialSeatAddon({
          quantity: Number(socialRes.data.quantity) || 0,
          grandfathered_quantity: Number(socialRes.data.grandfathered_quantity) || 0,
          status: String(socialRes.data.status || ''),
        });
      } else {
        setSocialSeatAddon(null);
      }

      if (!hoursRes.error && hoursRes.data) {
        setServiceHourRetainer({
          quantity: Number(hoursRes.data.quantity) || 0,
          status: String(hoursRes.data.status || ''),
        });
      } else {
        setServiceHourRetainer(null);
      }
    } catch {
      setAddonSubscriptions([]);
      setTeamSeatAddon(null);
      setSocialSeatAddon(null);
      setServiceHourRetainer(null);
    }
  };

  const handleOpenBillingPortal = async () => {
    if (!selectedBusiness?.id) return;
    setOpeningPortal(true);
    try {
      const { data, error: portalError } = await supabase.functions.invoke('stripe-subscription', {
        body: {
          action: 'create-billing-portal',
          businessId: selectedBusiness.id,
          returnUrl: window.location.href
        }
      });
      if (portalError || !data?.url) {
        toast.error('Unable to open billing portal');
        return;
      }
      window.location.href = data.url;
    } catch {
      toast.error('Unable to open billing portal');
    } finally {
      setOpeningPortal(false);
    }
  };

  const handleActivateNexusHosting = async (confirmCardOnFile = false) => {
    if (!selectedBusiness?.id) return;
    setActivatingNexusHosting(true);
    try {
      const origin = window.location.origin;
      const { data, error } = await createNexusHostingCheckout({
        businessId: selectedBusiness.id,
        successUrl: `${origin}/my-apps?nexus_hosting=activated&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${origin}/my-apps`,
        confirmCardOnFile,
      });
      if (error) {
        toast.error(error.message || 'Could not start hosting checkout');
        return;
      }
      const d = data as {
        success?: boolean;
        url?: string;
        code?: string;
        message?: string;
        activated?: boolean;
        requiresConfirmation?: boolean;
        card?: { brand?: string; last4?: string };
        amount?: number;
        currency?: string;
        interval?: string;
      };
      if (d?.code === 'NEEDS_BILLING_PORTAL') {
        toast.error(d.message || 'Update payment in billing first.');
        return;
      }
      if (d?.code === 'ALREADY_ACTIVE') {
        toast.success(d.message || 'Hosting is already active.');
        forceLoadBusinesses();
        return;
      }
      // A card on file exists — confirm before charging it.
      if (d?.success && d.requiresConfirmation) {
        const ok = window.confirm(`Activate website hosting?\n\n${describeCardCharge(d)}`);
        if (ok) await handleActivateNexusHosting(true);
        return;
      }
      // Card on file was charged directly — no checkout needed.
      if (d?.success && d.activated) {
        toast.success('Website hosting activated — your card on file was charged.');
        forceLoadBusinesses();
        return;
      }
      if (d?.success && d.url) {
        window.location.href = d.url;
        return;
      }
      toast.error(d?.message || 'Could not start hosting checkout');
    } finally {
      setActivatingNexusHosting(false);
    }
  };

  // ── Icon helpers (preserved from original) ──

  const customAppIcons: Record<string, string> = {
    'analytics': '/Analyticsappicon.png', 'projects': '/Projectsappicon.png',
    'calendar': '/Calenderappicon.png', 'crm': '/CRMappicon.png',
    'proposals': '/Proposalsappicon.png', 'tasks': '/Bookingsappicon.png',
    'inventory': '/Inventoryappicon.png', 'expenses': '/Expensesappicon.png',
    'payroll': '/Timesheetsappicon.png', 'teams': '/Teamsappicon.png',
    'invoices': '/Invoicesappicon.png', 'automations': '/Automateappicon.png',
    'services': '/Servicesappicon.png', 'customers': '/Customersappicon.png',
    'marketing': '/Marketingappicon.png', 'gift-cards': '/Giftcardsappicon.png',
    'messages': '/Messagesappicon.png', 'bookings': '/Bookingsappicon.png',
    'products': '/Storeappicon.png', 'mobile-calendar': '/Fieldproappicon.png',
    'm-calendar': '/Fieldproappicon.png',
  };

  const getAppIconBySlug = (appSlug: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      'calendar': <Calendar className="h-5 w-5" />, 'mobile-calendar': <Smartphone className="h-5 w-5" />,
      'm-calendar': <Smartphone className="h-5 w-5" />, 'customers': <Users className="h-5 w-5" />,
      'automations': <Zap className="h-5 w-5" />, 'teams': <Users className="h-5 w-5" />,
      'tasks': <ListChecks className="h-5 w-5" />, 'analytics': <BarChart3 className="h-5 w-5" />,
      'crm': <Building2 className="h-5 w-5" />, 'inventory': <Package className="h-5 w-5" />,
      'payroll': <DollarSign className="h-5 w-5" />, 'services': <Wrench className="h-5 w-5" />,
      'bookings': <CalendarDays className="h-5 w-5" />, 'proposals': <FileText className="h-5 w-5" />,
      'marketing': <Megaphone className="h-5 w-5" />, 'invoices': <Receipt className="h-5 w-5" />,
      'messages': <MessageSquare className="h-5 w-5" />, 'products': <ShoppingCart className="h-5 w-5" />,
      'gift-cards': <Gift className="h-5 w-5" />, 'projects': <Folder className="h-5 w-5" />,
      'que': <ListChecks className="h-5 w-5" />,
    };
    return iconMap[appSlug] || <Package className="h-5 w-5" />;
  };

  const renderAppIcon = (appSlug: string, size = "w-10 h-10") => {
    if (customAppIcons[appSlug]) {
      return (
        <div className={`${size} rounded-lg overflow-hidden bg-white shadow-sm flex-shrink-0`}>
          <img src={customAppIcons[appSlug]} alt={appSlug} className="w-full h-full object-cover" draggable={false} style={{ imageRendering: '-webkit-optimize-contrast' as any }} />
        </div>
      );
    }
    return (
      <div className={`${size} bg-gradient-to-br from-blue-600 to-blue-500 rounded-lg flex items-center justify-center text-white`}>
        {getAppIconBySlug(appSlug)}
      </div>
    );
  };

  const getAppName = (appId: string) => {
    const nameMap: Record<string, string> = {
      'bookings-core-app': 'Bookings', 'calendar-core-app': 'Calendar',
      'customers-core-app': 'Customers', 'services-core-app': 'Services',
      'messages-core-app': 'Messages', 'invoices-core-app': 'Invoices',
      'automations-core-app': 'Automate', 'mobile-calendar-core-app': 'Field-Pro',
      'products-addon-app': 'Store', 'teams-core-app': 'Teams',
      'payroll-core-app': 'Payroll', 'crm-core-app': 'CRM',
      'marketing-core-app': 'Marketing', 'analytics-core-app': 'Analytics',
      'proposals-core-app': 'Proposals', 'tasks-core-app': 'Docs',
      'projects-core-app': 'Projects', 'inventory-core-app': 'Inventory',
      'expenses-core-app': 'Expenses', 'gift-cards-core-app': 'Gift Cards',
      'expenses-addon-app': 'Expenses', 'gift-cards-addon-app': 'Gift Cards',
    };
    const slug = appId.replace('-core-app', '').replace('-addon-app', '');
    return nameMap[appId] || slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, ' ');
  };

  const isCurrentPlan = (planId: string) => subscription?.active_plan_id === planId;

  // ── Handlers (preserved from original) ──

  const handleRemoveOneTeamSeat = async () => {
    if (!selectedBusiness?.id) {
      toast.error('No business selected');
      return;
    }
    setCancellingTeamSeat(true);
    try {
      const result = await removePlatformTeamSeat(selectedBusiness.id);
      if (!result.success) {
        toast.error(result.error);
        return;
      }
      toast.success('Removed one extra team seat from your subscription.');
      setTeamSeatRemoveConfirm(false);
      await Promise.all([fetchAddonSubscriptions(), refreshSubscription(), reloadAppSystem()]);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove seat');
    } finally {
      setCancellingTeamSeat(false);
    }
  };

  const handleCancelAddon = async (addon: any) => {
    if (!selectedBusiness?.id) { toast.error('No business selected'); return; }
    setCancellingAddon(true);
    try {
      const { data, error: removeError } = await supabase.functions.invoke('stripe-subscription', {
        body: { action: 'remove-addon-app', businessId: selectedBusiness.id, addonAppId: addon.addon_app_id }
      });
      if (removeError) { toast.error(`Failed to cancel addon: ${removeError.message || 'Unknown error'}`); return; }
      if (!data?.success) { toast.error(`Failed to cancel addon: ${data?.error || data?.message || 'Unknown error'}`); return; }
      toast.success(`${addon.addon_apps.name} has been cancelled successfully!`);
      setSelectedAddonForCancel(null);
      await reloadAppSystem();
      await refreshSubscription();
      await fetchAddonSubscriptions();
      setTimeout(() => window.location.reload(), 1500);
    } catch (err: any) {
      toast.error(`Failed to cancel addon: ${err?.message || 'Unknown error'}`);
    } finally {
      setCancellingAddon(false);
    }
  };

  const handleSwitchPlan = async (newPlan: SubscriptionPlan) => {
    if (!selectedBusiness?.id) { toast.error('No business selected'); return; }
    setSwitchingPlan(true);
    try {
      const { data: planData } = await supabase
        .from('subscription_plans').select('stripe_price_id').eq('plan_id', newPlan.plan_id).single();

      if (planData?.stripe_price_id) {
        const success = await activateSubscription(newPlan.plan_id);
        if (success) {
          toast.success(`Successfully switched to ${newPlan.plan_name}! Refreshing...`);
          setTimeout(() => window.location.reload(), 1500);
        } else {
          toast.error('Failed to initiate plan switch');
        }
      } else {
        const { data: fullPlanData } = await supabase
          .from('subscription_plans').select('max_resources').eq('plan_id', newPlan.plan_id).single();
        const resourceLimit = fullPlanData?.max_resources || 0;
        const { error: updateError } = await supabase
          .from('businesses')
          .update({ active_plan_id: newPlan.plan_id, resource_limit: resourceLimit, updated_at: new Date().toISOString() })
          .eq('id', selectedBusiness.id);
        if (updateError) { toast.error('Failed to switch plan'); return; }
        toast.success(`Successfully switched to ${newPlan.plan_name}! Refreshing...`);
        setTimeout(() => window.location.reload(), 1500);
      }
      setSelectedPlanForSwitch(null);
      setShowChangePlanModal(false);
    } catch {
      toast.error('Failed to switch plan');
    } finally {
      setSwitchingPlan(false);
    }
  };

  // ── Derived values ──

  const currentPlan = plans.find(p => p.plan_id === subscription?.active_plan_id);
  const basePlanPrice = currentPlan?.plan_price || 0;
  const seatUnitMonthly = (() => {
    const raw = import.meta.env.VITE_PLATFORM_EXTRA_TEAM_SEAT_MONTHLY_USD;
    const n = typeof raw === 'string' ? Number.parseFloat(raw) : Number.NaN;
    return Number.isFinite(n) && n >= 0 ? n : 9.99;
  })();
  const socialSeatUnitMonthly = (() => {
    const raw = import.meta.env.VITE_SOCIAL_CONNECTION_MONTHLY_USD;
    const n = typeof raw === 'string' ? Number.parseFloat(raw) : Number.NaN;
    if (Number.isFinite(n) && n >= 0) return n;
    const fromLabel = String(SOCIAL_CONNECTION_PRICE_LABEL).match(/[\d.]+/);
    const parsed = fromLabel ? Number.parseFloat(fromLabel[0]) : Number.NaN;
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 29;
  })();
  const hasActiveTeamSeatAddon =
    teamSeatAddon?.status === 'active' && (teamSeatAddon?.quantity ?? 0) > 0;
  const teamSeatMonthlyEstimate =
    hasActiveTeamSeatAddon && teamSeatAddon
      ? teamSeatAddon.quantity * seatUnitMonthly
      : 0;
  const hasActiveSocialSeatAddon =
    socialSeatAddon?.status === 'active' && (socialSeatAddon?.quantity ?? 0) > 0;
  const socialSeatMonthlyEstimate =
    hasActiveSocialSeatAddon && socialSeatAddon
      ? socialSeatAddon.quantity * socialSeatUnitMonthly
      : 0;
  const hasActiveServiceHourRetainer =
    serviceHourRetainer?.status === 'active' && (serviceHourRetainer?.quantity ?? 0) > 0;
  const serviceHourRetainerMonthly =
    hasActiveServiceHourRetainer && serviceHourRetainer
      ? serviceHourRetainer.quantity * SERVICE_HOUR_RATE_USD
      : 0;
  const addonAppTotal = addonSubscriptions.reduce(
    (sum, a) => sum + (a.addon_apps?.price_monthly || 0),
    0,
  );
  const addonTotal =
    addonAppTotal + teamSeatMonthlyEstimate + socialSeatMonthlyEstimate + serviceHourRetainerMonthly;
  const nexusHostingMonthlyUsd = hostingMonthlyUsd;
  /** Installed-apps total: Nexus plan + add-ons only (website hosting is billed separately). */
  const totalMonthly = basePlanPrice + addonTotal;
  const addonLineCount =
    addonSubscriptions.length +
    (hasActiveTeamSeatAddon ? 1 : 0) +
    (hasActiveSocialSeatAddon ? 1 : 0) +
    (hasActiveServiceHourRetainer ? 1 : 0);
  const isBusinessOwner =
    !!(
      selectedBusiness?.owner_id &&
      sessionUserId &&
      selectedBusiness.owner_id === sessionUserId
    );
  const stripeDetails = subscription?.stripeDetails;

  const hasHostingStripeSub = Boolean(
    (selectedBusiness?.nexus_hosting_stripe_subscription_id || '').toString().trim(),
  );
  const hostingStripeStatusRaw = (
    hostingStripeDetails?.status ||
    selectedBusiness?.nexus_hosting_subscription_status ||
    ''
  )
    .toString()
    .toLowerCase();
  const hostingDisplayAmount =
    hostingStripeDetails != null && Number.isFinite(hostingStripeDetails.amount)
      ? hostingStripeDetails.amount
      : nexusHostingMonthlyUsd;
  const hostingBadgeLabel =
    hostingStripeStatusRaw === 'past_due' || hostingStripeStatusRaw === 'unpaid'
      ? 'Past due'
      : hostingStripeStatusRaw === 'trialing'
        ? 'Trialing'
        : hostingStripeStatusRaw === 'active'
          ? 'Active'
          : hostingStripeStatusRaw === 'canceled' || hostingStripeStatusRaw === 'cancelled'
            ? 'Canceled'
            : hostingStripeStatusRaw
              ? hostingStripeStatusRaw.charAt(0).toUpperCase() + hostingStripeStatusRaw.slice(1)
              : 'Inactive';

  const rawNexusUrl = (selectedBusiness?.nexus_website_url || '').trim();
  const nexusSiteHref = rawNexusUrl
    ? /^https?:\/\//i.test(rawNexusUrl)
      ? rawNexusUrl
      : `https://${rawNexusUrl}`
    : '';
  const nexusSiteConnected = Boolean(rawNexusUrl);
  const nexusHostingLive = isNexusHostingActive(selectedBusiness?.nexus_hosting_subscription_status);
  const nexusHostingPastDue =
    hostingStripeStatusRaw === 'past_due' || hostingStripeStatusRaw === 'unpaid';

  // ── Loading / Error states ──

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading subscription...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={fetchSubscriptionPlans} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">Try Again</button>
        </div>
      </div>
    );
  }

  // ── Render ──

  return (
    <div className="flex-1 overflow-auto bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-5 md:py-8">
          <button
            onClick={() => navigate('/app-store')}
            className="flex items-center text-gray-500 hover:text-gray-900 transition-colors mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            <span className="text-sm font-medium">Back to Nexus Connect</span>
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">My Apps & Subscription</h1>
          <p className="text-sm text-gray-500 mt-1">Plan & apps together; website & hosting together.</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-6">

        {/* ─── Nexus plan, apps & add-ons (single card) ─── */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-5 md:p-6">
            {subscription ? (
              <>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0">
                      <Crown className="h-6 w-6 text-white" aria-hidden />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-lg font-semibold text-gray-900">{currentPlan?.plan_name || 'Base Plan'}</h2>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="text-2xl font-bold text-gray-900 tabular-nums">${basePlanPrice}</span>
                        <span className="text-sm text-gray-500">/month</span>
                      </div>
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${
                      subscription.subscription_status === 'active'
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : subscription.subscription_status === 'past_due'
                          ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                          : 'bg-gray-100 text-gray-600 border border-gray-200'
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                        subscription.subscription_status === 'active'
                          ? 'bg-green-500'
                          : subscription.subscription_status === 'past_due'
                            ? 'bg-yellow-500'
                            : 'bg-gray-400'
                      }`}
                    />
                    {subscription.subscription_status === 'active'
                      ? 'Active'
                      : subscription.subscription_status === 'past_due'
                        ? 'Past Due'
                        : subscription.subscription_status?.charAt(0).toUpperCase() +
                            subscription.subscription_status?.slice(1) || 'Inactive'}
                  </span>
                </div>

                {stripeDetails && (
                  <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-xl">
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-gray-400 font-medium">Next Billing Date</p>
                      <p className="text-sm font-semibold text-gray-900 mt-0.5">
                        {new Date(stripeDetails.renewalDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-gray-400 font-medium">Current Period</p>
                      <p className="text-sm font-semibold text-gray-900 mt-0.5">
                        {new Date(stripeDetails.currentPeriodStart).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })}
                        {' — '}
                        {new Date(stripeDetails.currentPeriodEnd).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <p className="text-[11px] uppercase tracking-wider text-gray-400 font-medium">Billing Interval</p>
                      <p className="text-sm font-semibold text-gray-900 mt-0.5 capitalize">
                        {stripeDetails.interval || 'Monthly'}
                      </p>
                    </div>
                  </div>
                )}

                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => setShowChangePlanModal(true)}
                    className="px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Change Plan
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenBillingPortal}
                    disabled={openingPortal}
                    className="px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {openingPortal ? (
                      <>
                        <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-hidden />
                        Opening…
                      </>
                    ) : (
                      <>
                        <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                        Manage Billing
                      </>
                    )}
                  </button>
                </div>

                <div className="border-t border-gray-200 mt-6 pt-6" />
              </>
            ) : null}

            <div className="flex items-start justify-between gap-3 mb-3">
              <button
                type="button"
                onClick={() => setIncludedAppsExpanded((v) => !v)}
                aria-expanded={includedAppsExpanded}
                className="flex items-start gap-2 text-left min-w-0 flex-1 rounded-lg -m-1 p-1 hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              >
                <ChevronDown
                  className={`h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5 transition-transform duration-200 ${includedAppsExpanded ? '' : '-rotate-90'}`}
                  aria-hidden
                />
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-gray-900">Included Apps</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {currentPlan?.included_apps?.length || 0} included in plan
                    {addonLineCount > 0 &&
                      ` + ${addonLineCount} add-on${addonLineCount > 1 ? 's' : ''}`}
                  </p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => navigate('/app-store')}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors flex-shrink-0 pt-0.5"
              >
                Browse Apps
              </button>
            </div>

            {/* Base plan apps grid */}
            {includedAppsExpanded && currentPlan?.included_apps && currentPlan.included_apps.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 mb-5">
                {currentPlan.included_apps
                  // Field-Pro is retired — hidden even if still present in plan data.
                  .filter((appId) => !appId.startsWith('mobile-calendar') && !appId.startsWith('m-calendar'))
                  .map((appId) => {
                  const slug = appId.replace('-addon-app', '').replace('-core-app', '');
                  return (
                    <div key={appId} className="flex flex-col items-center gap-1.5 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                      {renderAppIcon(slug, "w-11 h-11")}
                      <span className="text-[11px] font-medium text-gray-700 text-center leading-tight">{getAppName(appId)}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add-on apps + paid team seats + social connections */}
            {addonLineCount > 0 && (
              <div className="border-t border-gray-100 pt-4 mt-1">
                <button
                  type="button"
                  onClick={() => setAddonsExpanded((v) => !v)}
                  aria-expanded={addonsExpanded}
                  className="flex items-center gap-2 w-full text-left rounded-lg -mx-1 px-1 py-1 mb-1 hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                >
                  <ChevronDown
                    className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${addonsExpanded ? '' : '-rotate-90'}`}
                    aria-hidden
                  />
                  <span className="text-xs uppercase tracking-wider text-gray-400 font-medium">Add-ons</span>
                </button>
                {addonsExpanded && (
                  <div className="space-y-2">
                    {addonSubscriptions.map((addon) => {
                      let appSlug = '';
                      if (addon.addon_apps?.app?.slug) appSlug = addon.addon_apps.app.slug;
                      else if (addon.addon_apps?.app_id) appSlug = addon.addon_apps.app_id.replace('-addon-app', '').replace('-core-app', '');
                      const appName = addon.addon_apps?.app?.name || addon.addon_apps?.name || 'Unknown App';

                      return (
                        <div key={addon.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors group">
                          <div className="flex items-center gap-3">
                            {renderAppIcon(appSlug, "w-9 h-9")}
                            <div>
                              <p className="text-sm font-medium text-gray-900">{appName}</p>
                              <p className="text-xs text-gray-500">${addon.addon_apps.price_monthly}/mo</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setSelectedAddonForCancel(addon)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                            title="Remove add-on"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                    {hasActiveTeamSeatAddon && teamSeatAddon && (
                      <div className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors group border border-emerald-100 bg-emerald-50/40">
                        <div className="flex items-center gap-3">
                          {renderAppIcon('teams', 'w-9 h-9')}
                          <div>
                            <p className="text-sm font-medium text-gray-900">Extra team member seats</p>
                            <p className="text-xs text-gray-600">
                              {teamSeatAddon.quantity} paid seat{teamSeatAddon.quantity !== 1 ? 's' : ''} · ~
                              ${seatUnitMonthly.toFixed(2)}/seat/mo (estimate)
                            </p>
                          </div>
                        </div>
                        {isBusinessOwner ? (
                          <button
                            type="button"
                            onClick={() => setTeamSeatRemoveConfirm(true)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                            title="Remove one paid seat"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        ) : (
                          <span className="text-[11px] text-gray-500 px-2">Owner manages</span>
                        )}
                      </div>
                    )}
                    {hasActiveSocialSeatAddon && socialSeatAddon && (
                      <div className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors group border border-sky-100 bg-sky-50/40">
                        <div className="flex items-center gap-3">
                          {renderAppIcon('marketing', 'w-9 h-9')}
                          <div>
                            <p className="text-sm font-medium text-gray-900">Social connections</p>
                            <p className="text-xs text-gray-600">
                              {socialSeatAddon.quantity} paid connection
                              {socialSeatAddon.quantity !== 1 ? 's' : ''}
                              {socialSeatAddon.grandfathered_quantity > 0
                                ? ` · ${socialSeatAddon.grandfathered_quantity} grandfathered`
                                : ''}{' '}
                              · ~${socialSeatUnitMonthly.toFixed(2)}/connection/mo (estimate)
                            </p>
                          </div>
                        </div>
                        <span className="text-[11px] text-gray-500 px-2 text-right max-w-[120px]">
                          Disconnect a profile to stop billing
                        </span>
                      </div>
                    )}
                    {hasActiveServiceHourRetainer && serviceHourRetainer && (
                      <div className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors group border border-slate-200 bg-slate-50/50">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center">
                            <Clock className="h-4 w-4 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Service hour retainer</p>
                            <p className="text-xs text-gray-600">
                              {serviceHourRetainer.quantity} hr
                              {serviceHourRetainer.quantity !== 1 ? 's' : ''}/mo · $
                              {SERVICE_HOUR_RATE_USD}/hr · unused hours roll over
                            </p>
                          </div>
                        </div>
                        <span className="text-[11px] text-gray-500 px-2 text-right max-w-[120px]">
                          Manage below
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Total — plan + add-ons only (hosting is a separate Stripe subscription) */}
            <div className="border-t border-gray-200 mt-5 pt-4">
              <div className="flex items-end justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-600">Total monthly cost</p>
                  <p className="text-xs text-gray-500 mt-1 leading-snug">
                    {addonLineCount > 0
                      ? 'Nexus plan plus add-ons (estimate before taxes). Website hosting is a separate subscription — see the next card.'
                      : 'Nexus plan only (estimate before taxes). Website hosting is separate — see the next card.'}
                  </p>
                </div>
                <p className="text-xl font-bold text-gray-900 tabular-nums shrink-0">
                  ${totalMonthly.toFixed(2)}
                  <span className="text-sm font-normal text-gray-500">/mo</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {selectedBusiness?.id && (
          <MyAppsServiceHoursCard
            businessId={selectedBusiness.id}
            isBusinessOwner={isBusinessOwner}
          />
        )}

        {/* ─── Website & hosting (single card: subscription + live URL + preview) ─── */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-5 md:p-6 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-600 to-teal-700 flex items-center justify-center shadow-sm flex-shrink-0">
                  <Globe className="h-6 w-6 text-white" aria-hidden />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900">Website & hosting</h2>
                  <p className="text-sm text-gray-500 mt-1 leading-snug">
                    Your live site URL and its own Stripe subscription — separate from your Nexus plan.
                  </p>
                </div>
              </div>
              {hasHostingStripeSub ? (
                <div className="flex flex-wrap items-center gap-2 justify-end sm:pt-0.5">
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                      hostingStripeStatusRaw === 'active' || hostingStripeStatusRaw === 'trialing'
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : hostingStripeStatusRaw === 'past_due' || hostingStripeStatusRaw === 'unpaid'
                          ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                          : 'bg-gray-100 text-gray-600 border border-gray-200'
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                        hostingStripeStatusRaw === 'active' || hostingStripeStatusRaw === 'trialing'
                          ? 'bg-green-500'
                          : hostingStripeStatusRaw === 'past_due' || hostingStripeStatusRaw === 'unpaid'
                            ? 'bg-yellow-500'
                            : 'bg-gray-400'
                      }`}
                    />
                    {hostingBadgeLabel}
                  </span>
                </div>
              ) : null}
            </div>

            {hasHostingStripeSub && selectedBusiness ? (
              <div className="rounded-xl border border-gray-200 bg-gray-50/90 p-4 md:p-5 space-y-4">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Hosting subscription</p>
                  <p className="text-2xl font-bold text-gray-900 tabular-nums mt-1">
                    ${hostingDisplayAmount.toFixed(2)}
                    <span className="text-sm font-normal text-gray-500">/month</span>
                  </p>
                </div>

                {hostingStripeLoading ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-white rounded-xl border border-gray-100 animate-pulse">
                    <div>
                      <div className="h-3 w-24 bg-gray-200 rounded mb-2" />
                      <div className="h-4 w-28 bg-gray-200 rounded" />
                    </div>
                    <div>
                      <div className="h-3 w-20 bg-gray-200 rounded mb-2" />
                      <div className="h-4 w-36 bg-gray-200 rounded" />
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <div className="h-3 w-24 bg-gray-200 rounded mb-2" />
                      <div className="h-4 w-16 bg-gray-200 rounded" />
                    </div>
                  </div>
                ) : hostingStripeDetails ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-white rounded-xl border border-gray-100">
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-gray-400 font-medium">Next billing date</p>
                      <p className="text-sm font-semibold text-gray-900 mt-0.5">
                        {formatHostingBillDate(hostingStripeDetails.renewalDate, true)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-gray-400 font-medium">Current period</p>
                      <p className="text-sm font-semibold text-gray-900 mt-0.5">
                        {formatHostingBillDate(hostingStripeDetails.currentPeriodStart, false)}
                        {' — '}
                        {formatHostingBillDate(hostingStripeDetails.currentPeriodEnd, false)}
                      </p>
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <p className="text-[11px] uppercase tracking-wider text-gray-400 font-medium">Billing interval</p>
                      <p className="text-sm font-semibold text-gray-900 mt-0.5 capitalize">
                        {hostingStripeDetails.interval || 'month'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 rounded-xl border border-amber-100 bg-amber-50/70 text-sm text-amber-950 space-y-1.5">
                    <p>
                      Could not load renewal dates from the server. Your hosting subscription is still on file — use{' '}
                      <span className="font-semibold">Manage billing</span> for invoices and your payment method.
                    </p>
                    <p className="text-xs text-amber-900/90">
                      If this never clears, confirm the latest <span className="font-mono">get-stripe-subscription</span>{' '}
                      Edge Function is deployed (it must accept{' '}
                      <span className="font-mono">subscriptionKind: &quot;nexus_hosting&quot;</span>).
                    </p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleOpenBillingPortal}
                  disabled={openingPortal}
                  className="px-4 py-2.5 border border-gray-300 bg-white text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {openingPortal ? (
                    <>
                      <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-hidden />
                      Opening…
                    </>
                  ) : (
                    <>
                      <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                      Manage billing
                    </>
                  )}
                </button>
              </div>
            ) : null}

            {nexusSiteConnected && !hasHostingStripeSub ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 md:p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-emerald-950">Activate website hosting</p>
                    <p className="text-xl font-bold text-emerald-950 tabular-nums mt-1">
                      ${nexusHostingMonthlyUsd.toFixed(2)}
                      <span className="text-sm font-semibold text-emerald-800">/mo</span>
                    </p>
                    <p className="text-xs text-emerald-900/85 mt-1 max-w-lg">
                      Separate Stripe subscription. Secure checkout; manage or cancel anytime in billing.
                    </p>
                  </div>
                  {isBusinessOwner ? (
                    <button
                      type="button"
                      onClick={() => handleActivateNexusHosting()}
                      disabled={activatingNexusHosting}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 shadow-sm shrink-0"
                    >
                      {activatingNexusHosting ? (
                        <RefreshCw className="h-4 w-4 animate-spin" aria-hidden />
                      ) : null}
                      Activate hosting
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {nexusSiteConnected ? (
              <>
                <div className="border-t border-gray-200" />
                <div className="space-y-4">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Live site</p>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <a
                        href={nexusSiteHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 break-all min-w-0 font-medium"
                      >
                        {rawNexusUrl}
                        <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
                      </a>
                      <a
                        href={nexusSiteHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-sm shrink-0 self-start sm:self-auto"
                      >
                        Visit site
                      </a>
                    </div>
                  </div>

                  {nexusHostingPastDue && hasHostingStripeSub ? (
                    <p className="text-xs text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                      Hosting is past due — use <span className="font-semibold">Manage billing</span> above to pay or
                      update your card.
                    </p>
                  ) : null}

                  <div className="relative rounded-xl border border-gray-200 overflow-hidden bg-gray-100 shadow-inner">
                    {!nexusHostingLive ? (
                      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-white/75 backdrop-blur-[1px] px-4 text-center">
                        <p className="text-sm font-medium text-gray-800">Preview only</p>
                        <p className="text-xs text-gray-600 max-w-sm">
                          Activate hosting for{' '}
                          <span className="font-semibold text-gray-800">${nexusHostingMonthlyUsd.toFixed(2)}/mo</span> to
                          use this URL as billed live hosting.
                        </p>
                      </div>
                    ) : null}
                    <iframe
                      key={nexusSiteHref}
                      src={nexusSiteHref}
                      title="Your Nexus website preview"
                      className={`w-full h-[min(52vh,520px)] min-h-[260px] border-0 bg-white block ${!nexusHostingLive ? 'opacity-60' : ''}`}
                      loading="lazy"
                      referrerPolicy="strict-origin-when-cross-origin"
                    />
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    If the preview stays blank, the site may block embedding. Use <span className="font-medium text-gray-600">Visit site</span> to open it in a full tab.
                  </p>

                  {/* Domains: preview URL + connect-your-own-domain with DNS records */}
                  {nexusHostingLive && (selectedBusiness as any)?.vercel_project_id && selectedBusiness?.id ? (
                    <CustomDomainPanel businessId={selectedBusiness.id} />
                  ) : null}
                </div>
              </>
            ) : selectedBusiness?.id ? (
              <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/70 p-4 md:p-5 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <Globe className="h-5 w-5 text-gray-600" aria-hidden />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">No website connected</p>
                    <p className="text-sm text-gray-600 mt-0.5">
                      A website for {selectedBusiness.name || 'this business'} will show here once it is connected.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/websites')}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50"
                >
                  Open website page
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* ─── CHANGE PLAN MODAL ─── */}
      {showChangePlanModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Change Plan</h3>
                <p className="text-sm text-gray-500 mt-0.5">Select a plan that fits your business</p>
              </div>
              <button onClick={() => setShowChangePlanModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="overflow-y-auto p-6">
              <div className={`grid grid-cols-1 md:grid-cols-2 gap-5 ${plans.length >= 4 ? 'xl:grid-cols-4' : 'lg:grid-cols-3'}`}>
                {plans.map((plan) => (
                  <div
                    key={plan.plan_id}
                    className={`rounded-2xl border-2 p-5 relative transition-all duration-200 ${
                      isCurrentPlan(plan.plan_id)
                        ? 'border-blue-500 bg-blue-50/30 ring-1 ring-blue-200'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    {isCurrentPlan(plan.plan_id) && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-[11px] font-semibold flex items-center gap-1">
                          <Crown className="h-3 w-3" /> Current
                        </span>
                      </div>
                    )}

                    <div className="text-center mb-5 pt-1">
                      <h4 className="text-lg font-bold text-gray-900">{plan.plan_name}</h4>
                      <div className="mt-1">
                        <span className="text-3xl font-bold text-gray-900">${plan.plan_price}</span>
                        <span className="text-sm text-gray-500">/mo</span>
                      </div>
                      <p className="mt-1.5 text-xs font-medium text-blue-700">AI included</p>
                    </div>

                    <div className="mb-5">
                      <p className="text-[11px] uppercase tracking-wider text-gray-400 font-medium mb-2">Included Apps</p>
                      <div className="space-y-1.5">
                        {plan.included_apps
                          .filter((appId) => !appId.startsWith('mobile-calendar') && !appId.startsWith('m-calendar'))
                          .map((appId) => (
                          <div key={appId} className="flex items-center gap-2 text-sm text-gray-700">
                            {renderAppIcon(appId.replace('-addon-app', '').replace('-core-app', ''), "w-7 h-7")}
                            <span className="text-xs font-medium">{getAppName(appId)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {plan.included_power_ups.length > 0 && (
                      <div className="mb-5">
                        <p className="text-[11px] uppercase tracking-wider text-gray-400 font-medium mb-2">Power-ups</p>
                        <div className="space-y-1.5">
                          {plan.included_power_ups.map((pid) => (
                            <div key={pid} className="flex items-center gap-2 text-xs text-gray-600">
                              <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <Zap className="h-3 w-3 text-purple-600" />
                              </div>
                              <span className="capitalize">{pid.replace('-powerup', '').replace('-', ' ')}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="pt-4 border-t border-gray-100">
                      {isCurrentPlan(plan.plan_id) ? (
                        <button disabled className="w-full py-2.5 px-4 bg-gray-100 text-gray-400 rounded-lg text-sm font-medium cursor-not-allowed">
                          Current Plan
                        </button>
                      ) : (
                        <button
                          className="w-full py-2.5 px-4 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                          onClick={() => setSelectedPlanForSwitch(plan)}
                        >
                          Switch to {plan.plan_name}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── PLAN SWITCH CONFIRMATION MODAL ─── */}
      {selectedPlanForSwitch && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Confirm Plan Switch</h3>
                <button onClick={() => setSelectedPlanForSwitch(null)} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="flex items-center justify-center mb-4">
                  <span className="text-gray-400 text-sm">{currentPlan?.plan_name || 'Current Plan'}</span>
                  <ArrowRight className="h-4 w-4 text-gray-400 mx-3" />
                  <span className="font-semibold text-blue-600 text-sm">{selectedPlanForSwitch.plan_name}</span>
                </div>
                <p className="text-gray-600 text-sm mb-4">
                  Are you sure you want to switch to the {selectedPlanForSwitch.plan_name}?
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                  <div className="text-2xl font-bold text-blue-900">${selectedPlanForSwitch.plan_price}/month</div>
                  <div className="text-sm text-blue-700 mt-1">
                    {selectedPlanForSwitch.included_apps.length} apps included
                    {selectedPlanForSwitch.included_power_ups.length > 0 &&
                      ` + ${selectedPlanForSwitch.included_power_ups.length} power-ups`
                    }
                  </div>
                </div>
                <p className="text-xs text-gray-500">Your subscription will be updated immediately with prorated billing.</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedPlanForSwitch(null)}
                  className="flex-1 py-2.5 px-4 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSwitchPlan(selectedPlanForSwitch)}
                  disabled={switchingPlan}
                  className="flex-1 py-2.5 px-4 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {switchingPlan ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Switching...</>
                  ) : (
                    'Switch Plan'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── ADDON CANCEL CONFIRMATION MODAL ─── */}
      {teamSeatRemoveConfirm && hasActiveTeamSeatAddon && teamSeatAddon && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Remove extra team seat</h3>
                <button
                  type="button"
                  onClick={() => setTeamSeatRemoveConfirm(false)}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-600 text-sm mb-4">
                Remove <strong>one</strong> paid team seat from your subscription? You currently have{' '}
                <strong>{teamSeatAddon.quantity}</strong> extra seat{teamSeatAddon.quantity !== 1 ? 's' : ''}. Your
                next invoice will be prorated.
              </p>
              <p className="text-xs text-gray-500 mb-5">
                If you are over your plan&apos;s included seats after this, you will need to deactivate team members
                or add seats again from the Team page.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setTeamSeatRemoveConfirm(false)}
                  className="flex-1 py-2.5 px-4 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Keep seats
                </button>
                <button
                  type="button"
                  onClick={handleRemoveOneTeamSeat}
                  disabled={cancellingTeamSeat}
                  className="flex-1 py-2.5 px-4 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {cancellingTeamSeat ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Removing…
                    </>
                  ) : (
                    'Remove 1 seat'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedAddonForCancel && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Cancel Add-on</h3>
                <button onClick={() => setSelectedAddonForCancel(null)} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Check className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-lg">{selectedAddonForCancel.addon_apps.name}</h4>
                  <p className="text-sm text-gray-600">${selectedAddonForCancel.addon_apps.price_monthly}/month</p>
                </div>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Are you sure you want to cancel <strong>{selectedAddonForCancel.addon_apps.name}</strong>?
              </p>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <X className="h-3 w-3 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-red-800 font-medium">You will lose access immediately</p>
                    <p className="text-xs text-red-700 mt-0.5">The app will be removed from your navigation and all features will be disabled.</p>
                  </div>
                </div>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                <div className="text-center">
                  <p className="text-sm text-green-700">You'll save</p>
                  <p className="text-2xl font-bold text-green-900">${selectedAddonForCancel.addon_apps.price_monthly}/mo</p>
                  <p className="text-xs text-green-600 mt-0.5">Your next invoice will be prorated</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 text-center mb-5">You can reinstall this add-on anytime from Nexus Connect.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedAddonForCancel(null)}
                  className="flex-1 py-2.5 px-4 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Keep Add-on
                </button>
                <button
                  onClick={() => handleCancelAddon(selectedAddonForCancel)}
                  disabled={cancellingAddon}
                  className="flex-1 py-2.5 px-4 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {cancellingAddon ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Cancelling...</>
                  ) : (
                    'Cancel Add-on'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
